import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ClipboardList } from 'lucide-react';
import { EVENTO_LABEL } from '../../lib/labels';

const hoyISO = () => new Date().toISOString().slice(0, 10);
const TIPOS = ['entrada', 'inicio_descanso', 'fin_descanso', 'fin_jornada'];

const FichajesTab = () => {
  const [empleados, setEmpleados] = useState([]);
  const [username, setUsername] = useState('todos');
  const [fecha, setFecha] = useState(hoyISO());
  const [fichajes, setFichajes] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [form, setForm] = useState({ user_id: '', tipo: 'entrada', timestamp: '', motivo: '' });
  const [editForm, setEditForm] = useState({ id: '', tipo: 'entrada', timestamp: '', motivo: '' });

  const cargarEmpleados = () => api.get('/admin/empleados').then((r) => setEmpleados(r.data)).catch(() => {});

  const cargarFichajes = () => {
    const params = { fecha };
    if (username !== 'todos') params.username = username;
    api.get('/admin/fichajes', { params }).then((r) => setFichajes(r.data)).catch(() => {});
  };

  useEffect(() => { cargarEmpleados(); }, []);
  useEffect(() => { cargarFichajes(); /* eslint-disable-next-line */ }, [username, fecha]);

  const abrirAdd = () => {
    const emp = username !== 'todos' ? empleados.find((e) => e.username === username) : empleados[0];
    setForm({ user_id: emp?.id || '', tipo: 'entrada', timestamp: `${fecha}T09:00`, motivo: '' });
    setOpenAdd(true);
  };

  const crear = async () => {
    if (!form.user_id || !form.timestamp) { toast.error('Completa empleado y fecha/hora'); return; }
    try {
      await api.post('/admin/fichajes', form);
      toast.success('Fichaje añadido');
      setOpenAdd(false);
      cargarFichajes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al añadir');
    }
  };

  const abrirEdit = (f) => {
    setEditForm({ id: f.id, tipo: f.tipo, timestamp: f.timestamp.slice(0, 16), motivo: '' });
    setOpenEdit(true);
  };

  const guardarEdit = async () => {
    try {
      await api.put(`/admin/fichajes/${editForm.id}`, {
        tipo: editForm.tipo, timestamp: editForm.timestamp, motivo: editForm.motivo,
      });
      toast.success('Fichaje actualizado');
      setOpenEdit(false);
      cargarFichajes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const eliminar = async (f) => {
    const motivo = window.prompt('Motivo de la eliminación:', '');
    if (motivo === null) return;
    try {
      await api.delete(`/admin/fichajes/${f.id}`, { params: { motivo } });
      toast.success('Fichaje eliminado');
      cargarFichajes();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-6" data-testid="fichajes-tab">
      <Card className="border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <ClipboardList className="h-5 w-5" /> Fichajes — corrección manual
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1.5">
              <Label>Empleado</Label>
              <Select value={username} onValueChange={setUsername}>
                <SelectTrigger className="w-[220px]" data-testid="fichajes-employee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {empleados.map((e) => <SelectItem key={e.username} value={e.username}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Fecha</Label>
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-[160px]" data-testid="fichajes-fecha" />
            </div>
            <Button onClick={abrirAdd} className="bg-zinc-900 hover:bg-zinc-800" data-testid="add-fichaje-btn">
              <Plus className="h-4 w-4 mr-2" /> Añadir fichaje
            </Button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-zinc-100">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  <th className="py-2.5 px-4">Empleado</th>
                  <th className="py-2.5 px-4">Tipo</th>
                  <th className="py-2.5 px-4">Hora</th>
                  <th className="py-2.5 px-4">Origen</th>
                  <th className="py-2.5 px-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {fichajes.map((f) => (
                  <tr key={f.id} className="border-t border-zinc-100" data-testid={`fichaje-row-${f.id}`}>
                    <td className="py-2.5 px-4 font-medium text-zinc-900">{f.full_name}</td>
                    <td className="py-2.5 px-4">{EVENTO_LABEL[f.tipo]}</td>
                    <td className="py-2.5 px-4 tabular-nums">{new Date(f.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' })}</td>
                    <td className="py-2.5 px-4">
                      {f.manual && <Badge className="bg-blue-100 text-blue-800 mr-1">Manual</Badge>}
                      {f.editado && <Badge className="bg-amber-100 text-amber-800">Editado</Badge>}
                      {!f.manual && !f.editado && <span className="text-zinc-400 text-sm">Automático</span>}
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex justify-end gap-2">
                        <Button size="icon" variant="outline" onClick={() => abrirEdit(f)} data-testid={`edit-fichaje-${f.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="outline" className="text-red-600" onClick={() => eliminar(f)} data-testid={`delete-fichaje-${f.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {fichajes.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-zinc-400">No hay fichajes para este día.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Añadir fichaje manual</DialogTitle>
            <DialogDescription>Registra un fichaje olvidado. Quedará marcado como manual en la auditoría.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Empleado</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="add-fichaje-employee"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="add-fichaje-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => <SelectItem key={t} value={t}>{EVENTO_LABEL[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Fecha y hora</Label>
              <Input type="datetime-local" value={form.timestamp} onChange={(e) => setForm({ ...form, timestamp: e.target.value })} data-testid="add-fichaje-timestamp" />
            </div>
            <div className="space-y-1.5">
              <Label>Motivo</Label>
              <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Ej: olvido de fichaje" data-testid="add-fichaje-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAdd(false)}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={crear} data-testid="save-add-fichaje-btn">Añadir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar fichaje</DialogTitle>
            <DialogDescription>Modifica el tipo o la hora. El cambio quedará registrado en la auditoría.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={editForm.tipo} onValueChange={(v) => setEditForm({ ...editForm, tipo: v })}>
                <SelectTrigger data-testid="edit-fichaje-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => <SelectItem key={t} value={t}>{EVENTO_LABEL[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Fecha y hora</Label>
              <Input type="datetime-local" value={editForm.timestamp} onChange={(e) => setEditForm({ ...editForm, timestamp: e.target.value })} data-testid="edit-fichaje-timestamp" />
            </div>
            <div className="space-y-1.5">
              <Label>Motivo</Label>
              <Input value={editForm.motivo} onChange={(e) => setEditForm({ ...editForm, motivo: e.target.value })} data-testid="edit-fichaje-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEdit(false)}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={guardarEdit} data-testid="save-edit-fichaje-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FichajesTab;
